import React, { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { sendInquiry } from "../helpers/inquiryApi";
import MetaTags from "react-meta-tags";
import LayoutOne from "../layouts/LayoutOne";
import "../assets/css/recruitment-premium.css";
import CountUp from "react-countup";

const publicAsset = (path) =>
  `${process.env.PUBLIC_URL || ""}/assets/recruitment-pdf/${path}`;
const publicFile = (path) => `${process.env.PUBLIC_URL || ""}/${encodeURI(path)}`;

const ISO_CERTIFICATIONS = [
  {
    code: "ISO 9001",
    title: "Quality Management System",
    href: publicFile("assets/iso-certificates/SAC - Quality Management System  - ISO Certificate.pdf"),
  },
  {
    code: "ISO 14001",
    title: "Environmental Management System",
    href: publicFile("assets/iso-certificates/SAC - Environmental Management System - ISO 14001-2015.pdf"),
  },
  {
    code: "ISO 45001",
    title: "Occupational Health & Safety Management System",
    href: publicFile("assets/iso-certificates/SAC - Occupational Health & Management System -  ISO 45001-2018.pdf"),
  },
];

const TRUST_METRICS = [
  {
    title: "Operator-Led Organization",
    value: 100,
    suffix: "%",
    caption: "Field-first delivery DNA",
    icon: "fa fa-industry",
  },
  {
    title: "Global Talent Network",
    value: 4,
    suffix: "+ regions",
    caption: "India, GCC, SEA, Africa",
    icon: "fa fa-globe",
  },
  {
    title: "Onshore & Offshore Expertise",
    value: 18,
    suffix: "+ rigs",
    caption: "Operationally aligned manpower",
    icon: "fa fa-anchor",
  },
  {
    title: "Rapid Mobilization Capability",
    value: 72,
    suffix: " hrs",
    caption: "For urgent requirement response",
    icon: "fa fa-bolt",
  },
  {
    title: "International Compliance Support",
    value: 360,
    suffix: "°",
    caption: "Visa, docs, medical, deployment",
    icon: "fa fa-shield",
  },
];

const SERVICE_ITEMS = [
  {
    id: 1,
    title: "Technical Manpower Supply",
    icon: "fa fa-cogs",
    highlight: false,
    items: [
      "Drilling Engineers",
      "Drilling Supervisors",
      "Mud Engineers",
      "HSE Officers",
      "Geologists & Petrophysicists",
      "Production Engineers",
      "Wellsite Engineers",
    ],
  },
  {
    id: 2,
    title: "Complete Crew Deployment",
    icon: "fa fa-users",
    highlight: false,
    items: [
      "Roustabout to Toolpusher / OIM",
      "Onshore & Offshore crews",
      "Drilling & Workover operations",
    ],
  },
  {
    id: 3,
    title: "Refinery Shutdown & Turnaround Workforce",
    icon: "fa fa-fire",
    highlight: true,
    badge: "Priority vertical",
    badgeNote:
      "Mobilization-ready specialists for tight turnaround windows.",
    items: [
      "Welders & Fabricators",
      "Pipefitters",
      "E&I Technicians",
      "QA/QC Engineers",
      "Mechanical Supervisors",
      "Scaffolding Teams",
    ],
  },
  {
    id: 4,
    title: "EPC & Project-Based Workforce",
    icon: "fa fa-building",
    highlight: false,
    items: [
      "Project Engineers",
      "Construction Teams",
      "Commissioning Support",
      "Maintenance Teams",
    ],
  },
  {
    id: 5,
    title: "End-to-End Mobilization & Compliance",
    icon: "fa fa-plane",
    highlight: false,
    wide: true,
    items: [
      "Visa processing support",
      "Documentation & certifications",
      "Medicals & approvals",
      "Travel & deployment",
    ],
  },
];

const ENGAGEMENT_TIERS = [
  {
    key: "A",
    title: "Day Rate Model",
    items: [
      "Salary + Insurance + PPE + Travel",
      "Compliance & documentation",
      "Client handles local logistics",
    ],
  },
  {
    key: "B",
    title: "Full Deployment Model",
    items: ["Includes Option A", "SAC manages visa & extended logistics"],
  },
  {
    key: "C",
    title: "Recruitment Model",
    items: [
      "Candidate sourcing & screening",
      "Interview coordination",
      "Placement fee-based model",
    ],
  },
];

const WHY_POINTS = [
  "Operator-driven approach (not just recruitment)",
  "Technically screened & field-ready candidates",
  "Fast turnaround for urgent requirements",
  "Global talent network (India, GCC, SEA, Africa)",
  "Strong understanding of EPC & drilling operations",
];

const INDUSTRIES = [
  { label: "Oil & Gas Operators", icon: "fa fa-tint" },
  { label: "Drilling Contractors", icon: "fa fa-wrench" },
  { label: "EPC Companies", icon: "fa fa-sitemap" },
  { label: "Refineries & Petrochemical Plants", icon: "fa fa-industry" },
];

const REGIONS = [
  { label: "Middle East (GCC)", icon: "fa fa-map-marker" },
  { label: "Africa", icon: "fa fa-globe" },
  { label: "South Asia", icon: "fa fa-map" },
  { label: "Southeast Asia", icon: "fa fa-anchor" },
];

const PROCESS_STEPS = [
  {
    step: "01",
    title: "Requirement Analysis",
    detail: "Role, project timeline, site conditions, and HSE expectations.",
  },
  {
    step: "02",
    title: "Candidate Sourcing",
    detail: "Targeted shortlisting from SAC's operator-vetted talent pools.",
  },
  {
    step: "03",
    title: "Technical Screening",
    detail: "Trade checks, competency validation, and readiness verification.",
  },
  {
    step: "04",
    title: "Client Interviews",
    detail: "Interview scheduling, coordination, and final selection closure.",
  },
  {
    step: "05",
    title: "Documentation & Compliance",
    detail: "Visa, medicals, certifications, and statutory documentation.",
  },
  {
    step: "06",
    title: "Deployment & Mobilization",
    detail: "Travel logistics, onboarding, and project-site mobilization.",
  },
];

const SectionHeader = ({ kicker, title, intro, center }) => (
  <div className={center ? "text-center" : ""}>
    {kicker && (
      <p className="rec-section-kicker">{kicker}</p>
    )}
    <h2 className="rec-section-title">{title}</h2>
    <div
      className={`rec-section-rule ${center ? "rec-section-rule-center" : ""}`}
    />
    {intro && (
      <p className={`rec-section-intro ${center ? "" : "mb-0"}`}>{intro}</p>
    )}
  </div>
);

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());

const OverseasRecruitment = () => {
  const [inqCompany, setInqCompany] = useState("");
  const [inqContact, setInqContact] = useState("");
  const [inqEmail, setInqEmail] = useState("");
  const [inqPhone, setInqPhone] = useState("");
  const [inqMessage, setInqMessage] = useState("");
  const [inqHp, setInqHp] = useState("");
  const [inqStatus, setInqStatus] = useState({ type: "", text: "" });
  const [inqSubmitting, setInqSubmitting] = useState(false);

  const heroBg = `linear-gradient(115deg, rgba(10, 37, 64, 0.92) 0%, rgba(17, 70, 122, 0.72) 38%, rgba(10, 37, 64, 0.35) 100%), url(${publicAsset(
    "sac-mp-000.png"
  )})`;

  const ctaBg = `url(${publicAsset("sac-mp-021.png")})`;

  async function handleRecruitmentInquiry(e) {
    e.preventDefault();
    setInqStatus({ type: "", text: "" });

    if (!inqCompany.trim()) {
      setInqStatus({ type: "error", text: "Please enter your company name." });
      return;
    }
    if (!inqContact.trim()) {
      setInqStatus({ type: "error", text: "Please enter a contact person name." });
      return;
    }
    if (!emailOk(inqEmail)) {
      setInqStatus({ type: "error", text: "Please enter a valid email address." });
      return;
    }
    if (!inqMessage.trim()) {
      setInqStatus({ type: "error", text: "Please describe your manpower requirements." });
      return;
    }

    setInqSubmitting(true);
    try {
      await sendInquiry({
        type: "recruitment",
        source: "overseas-recruitment",
        company: inqCompany.trim(),
        name: inqContact.trim(),
        email: inqEmail.trim(),
        phone: inqPhone.trim(),
        message: inqMessage.trim(),
        _hp: inqHp,
      });
      setInqStatus({
        type: "ok",
        text: "Thank you — your inquiry has been sent. Our recruitment team will contact you shortly.",
      });
      setInqCompany("");
      setInqContact("");
      setInqEmail("");
      setInqPhone("");
      setInqMessage("");
      setInqHp("");
    } catch (err) {
      setInqStatus({
        type: "error",
        text: err.message || "Unable to send right now. Please try again or use the phone number above.",
      });
    } finally {
      setInqSubmitting(false);
    }
  }

  return (
    <Fragment>
      <MetaTags>
        <title>SAC Overseas Recruitment & Workforce Solutions</title>
        <meta
          name="description"
          content="Operator-driven workforce solutions for global energy projects. Technical manpower, complete crews, refinery turnaround teams, and EPC workforce from South Asia Consultancy."
        />
      </MetaTags>
      <LayoutOne>
        <div className="recruitment-page">
          <section
            className="rec-hero"
            style={{ backgroundImage: heroBg }}
          >
            <div className="container rec-hero-inner">
              <span className="rec-eyebrow">
                <i className="fa fa-bolt" aria-hidden="true" />
                Global energy workforce
              </span>
              <h1 className="rec-hero-title">
                Operator-Driven Workforce Solutions for Global Energy Projects
              </h1>
              <p className="rec-hero-lead">
                South Asia Consultancy (SAC) delivers deployment-ready technical
                manpower and complete crews for drilling, refinery shutdowns, and
                EPC projects — backed by real field operations experience.
              </p>
              <div className="rec-hero-cta">
                <Link to="/contact" className="rec-btn-primary">
                  Request Manpower
                  <i className="fa fa-arrow-right" aria-hidden="true" />
                </Link>
                <Link to="/contact" className="rec-btn-ghost">
                  Contact Us
                  <i className="fa fa-envelope-o" aria-hidden="true" />
                </Link>
              </div>
              <div className="rec-hero-stats">
                <div className="rec-stat-pill">
                  <strong>42+</strong>
                  <span>Active rigs under O&amp;M footprint</span>
                </div>
                <div className="rec-stat-pill">
                  <strong>4</strong>
                  <span>Major mobilization corridors</span>
                </div>
                <div className="rec-stat-pill">
                  <strong>Zero LTI</strong>
                  <span>HSE culture from the field</span>
                </div>
              </div>
            </div>
          </section>

          <section className="rec-section rec-iso-strip">
            <div className="container">
              <SectionHeader
                kicker="Certifications"
                title="Certified for Quality, Safety & Environmental Excellence"
                intro="SAC maintains internationally recognized management standards to support global energy clients."
                center
              />
              <div className="rec-iso-grid">
                {ISO_CERTIFICATIONS.map((iso) => (
                  <a
                    key={iso.code}
                    href={iso.href}
                    className="rec-iso-card"
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <div className="rec-iso-badge">{iso.code}</div>
                    <h3>{iso.title}</h3>
                    <span>View / Download Certificate</span>
                  </a>
                ))}
              </div>
            </div>
          </section>

          <section className="rec-section rec-section-alt">
            <div className="container">
              <SectionHeader
                kicker="Trust indicators"
                title="Operator Credibility at a Glance"
                center
              />
              <div className="rec-metric-grid">
                {TRUST_METRICS.map((metric) => (
                  <div className="rec-metric-card" key={metric.title}>
                    <i className={metric.icon} aria-hidden="true" />
                    <h3>
                      <CountUp end={metric.value} duration={2.2} />
                      {metric.suffix}
                    </h3>
                    <h4>{metric.title}</h4>
                    <p>{metric.caption}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="rec-section rec-section-alt">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-6 mb-5 mb-lg-0">
                  <SectionHeader
                    kicker="Who we are"
                    title="About SAC Recruitment"
                  />
                  <p className="rec-prose">
                    Unlike conventional recruitment agencies, SAC is built on
                    hands-on operational experience as an Oil Block Operator &amp;
                    Drilling Contractor (Onshore &amp; Offshore).
                  </p>
                  <p className="rec-prose">
                    We understand not only the roles, but the real field
                    conditions, HSE requirements, and operational pressures of
                    oil &amp; gas projects. This enables us to provide:
                  </p>
                  <div className="rec-chip-row">
                    <span className="rec-chip">
                      <i className="fa fa-check" aria-hidden="true" />
                      Field-ready manpower
                    </span>
                    <span className="rec-chip">
                      <i className="fa fa-check" aria-hidden="true" />
                      Faster deployment
                    </span>
                    <span className="rec-chip">
                      <i className="fa fa-check" aria-hidden="true" />
                      Performance alignment
                    </span>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="rec-media-frame">
                    <img
                      src={publicAsset("sac-mp-005.png")}
                      alt="SAC manpower and operations"
                      loading="lazy"
                    />
                    <div className="rec-media-badge">
                      <i className="fa fa-shield mr-2" aria-hidden="true" />
                      Operations-led screening &amp; deployment discipline
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="rec-section">
            <div className="container">
              <SectionHeader
                kicker="Capabilities"
                title="Our Services"
                intro="From specialist roles to full crews and turnaround teams — structured for how energy projects actually run."
                center
              />
              <div className="rec-bento">
                {SERVICE_ITEMS.map((svc) => (
                  <div
                    key={svc.id}
                    className={`rec-bento-span-${svc.wide ? 12 : 6}`}
                  >
                    <article
                      className={`rec-card ${
                        svc.highlight ? "rec-card-highlight" : ""
                      }`}
                    >
                      <div className="rec-card-icon">
                        <i className={svc.icon} aria-hidden="true" />
                      </div>
                      {svc.badge && (
                        <span className="rec-card-badge">{svc.badge}</span>
                      )}
                      <h3 className="rec-card-title">
                        {svc.id}. {svc.title}
                      </h3>
                      {svc.badgeNote && (
                        <p
                          className="rec-prose mb-2"
                          style={{ fontSize: "0.88rem", color: "#800000" }}
                        >
                          {svc.badgeNote}
                        </p>
                      )}
                      <ul>
                        {svc.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </article>
                  </div>
                ))}
              </div>
              <div className="rec-visual-band">
                <img
                  src={publicAsset("sac-mp-012.png")}
                  alt="Energy operations and workforce"
                  loading="lazy"
                />
              </div>
            </div>
          </section>

          <section className="rec-section rec-refinery-focus">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-6 mb-4 mb-lg-0">
                  <SectionHeader
                    kicker="Core vertical"
                    title="Refinery Shutdown & Turnaround Specialists"
                  />
                  <p className="rec-prose">
                    SAC deploys operator-ready shutdown teams for high-pressure
                    turnaround schedules with strict compliance and safety
                    controls.
                  </p>
                  <ul className="rec-check-list mb-0">
                    <li>Welders & Fabricators</li>
                    <li>Pipefitters</li>
                    <li>E&I Technicians</li>
                    <li>QA/QC Inspectors</li>
                    <li>Mechanical Supervisors</li>
                    <li>Scaffolding Teams</li>
                  </ul>
                </div>
                <div className="col-lg-6">
                  <div className="rec-media-frame">
                    <img
                      src={publicAsset("sac-mp-012.png")}
                      alt="Refinery shutdown and turnaround workforce"
                      loading="lazy"
                    />
                    <div className="rec-media-badge">
                      <i className="fa fa-fire mr-2" aria-hidden="true" />
                      Dedicated refinery turnaround workforce vertical
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="rec-section rec-section-alt">
            <div className="container">
              <SectionHeader
                kicker="Commercial models"
                title="Our Engagement Models"
                intro="Choose the commercial wrapper that matches how your project buys labour — from day-rate through to full mobilization."
                center
              />
              <div className="rec-tier-wrap">
                {ENGAGEMENT_TIERS.map((tier) => (
                  <div key={tier.key} className="rec-tier">
                    <div className="rec-tier-num">{tier.key}</div>
                    <h3>Option {tier.key} — {tier.title}</h3>
                    <ul>
                      {tier.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="rec-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-6 mb-5 mb-lg-0 order-lg-2">
                  <div className="rec-media-frame">
                    <img
                      src={publicAsset("sac-mp-013.png")}
                      alt="SAC recruitment process and corridors"
                      loading="lazy"
                    />
                    <div className="rec-media-badge">
                      <i className="fa fa-line-chart mr-2" aria-hidden="true" />
                      Embedded talent network &amp; verified corridors
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 order-lg-1">
                  <SectionHeader
                    kicker="Proof points"
                    title="Why Choose SAC?"
                  />
                  <ul className="rec-check-list">
                    {WHY_POINTS.map((pt) => (
                      <li key={pt}>{pt}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="rec-section">
            <div className="container">
              <SectionHeader
                kicker="Delivery methodology"
                title="SAC Recruitment Process"
                intro="Structured execution from technical requirement capture to mobilization."
                center
              />
              <div className="rec-process-grid">
                {PROCESS_STEPS.map((item) => (
                  <div className="rec-process-card" key={item.step}>
                    <span className="rec-process-step">{item.step}</span>
                    <h3>{item.title}</h3>
                    <p>{item.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="rec-section rec-section-dark">
            <div className="container">
              <SectionHeader
                kicker="Sectors"
                title="Industries We Serve"
                intro="Deep exposure across the value chain — from the wellhead to downstream turnaround."
                center
              />
              <div className="rec-industry-grid">
                {INDUSTRIES.map((ind) => (
                  <div key={ind.label} className="rec-industry-tile">
                    <i className={ind.icon} aria-hidden="true" />
                    <span>{ind.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="rec-section rec-section-alt">
            <div className="container">
              <SectionHeader
                kicker="Geographies"
                title="Global Reach"
                intro="We mobilize talent across proven energy corridors with an operator-led workforce model."
                center
              />
              <div className="row align-items-center">
                <div className="col-lg-5 mb-4 mb-lg-0">
                  <div className="rec-region-grid">
                    {REGIONS.map((r) => (
                      <div key={r.label} className="rec-region">
                        <i className={r.icon} aria-hidden="true" />
                        {r.label}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-lg-7">
                  <div className="rec-media-frame rec-map-frame">
                    <img
                      src={publicAsset("sac-mp-019.png")}
                      alt="Global reach map covering GCC, Africa, South Asia and Southeast Asia"
                      loading="lazy"
                    />
                    <div className="rec-map-tags">
                      <span>GCC / Middle East</span>
                      <span>Africa</span>
                      <span>South Asia</span>
                      <span>Southeast Asia</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section
            className="rec-cta-final text-white"
            style={{ backgroundImage: ctaBg }}
          >
            <div className="container rec-cta-inner">
              <h2>
                Looking for reliable manpower for your next project?
              </h2>
              <p className="rec-cta-sub">
                Let's discuss your manpower requirements, deployment timelines,
                and project needs.
              </p>
              <div className="rec-cta-glass">
                <Link to="/contact" className="rec-btn-primary">
                  Request a Proposal
                  <i className="fa fa-file-text-o" aria-hidden="true" />
                </Link>
                <a
                  href="tel:+919316305258"
                  className="rec-btn-ghost"
                >
                  Schedule a Call
                  <i className="fa fa-phone" aria-hidden="true" />
                </a>
              </div>
            </div>
          </section>

          <a href="#quick-inquiry" className="rec-sticky-cta">
            <i className="fa fa-paper-plane" aria-hidden="true" />
            Request Manpower
          </a>

          <section className="rec-section rec-quick-inquiry" id="quick-inquiry">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-8">
                  <div className="rec-inquiry-card">
                    <SectionHeader
                      kicker="Lead generation"
                      title="Quick Inquiry Form"
                      intro="Tell us your manpower requirements and our operator-led recruitment team will contact you quickly."
                      center
                    />
                    <form className="rec-inquiry-form" onSubmit={handleRecruitmentInquiry} noValidate>
                      {inqStatus.text ? (
                        <div
                          className={`alert ${inqStatus.type === "ok" ? "alert-success" : "alert-danger"} mb-3`}
                          role="status"
                        >
                          {inqStatus.text}
                        </div>
                      ) : null}
                      <div
                        className="position-absolute"
                        style={{ left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}
                        aria-hidden="true"
                      >
                        <label htmlFor="rec-inq-hp">Leave blank</label>
                        <input
                          type="text"
                          id="rec-inq-hp"
                          tabIndex={-1}
                          autoComplete="off"
                          value={inqHp}
                          onChange={(e) => setInqHp(e.target.value)}
                        />
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Company Name"
                            aria-label="Company Name"
                            value={inqCompany}
                            onChange={(e) => setInqCompany(e.target.value)}
                            autoComplete="organization"
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Contact Person"
                            aria-label="Contact Person"
                            value={inqContact}
                            onChange={(e) => setInqContact(e.target.value)}
                            autoComplete="name"
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            type="email"
                            className="form-control"
                            placeholder="Email Address"
                            aria-label="Email Address"
                            value={inqEmail}
                            onChange={(e) => setInqEmail(e.target.value)}
                            autoComplete="email"
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Phone Number"
                            aria-label="Phone Number"
                            value={inqPhone}
                            onChange={(e) => setInqPhone(e.target.value)}
                            autoComplete="tel"
                          />
                        </div>
                        <div className="col-12 mb-3">
                          <textarea
                            className="form-control"
                            rows="4"
                            placeholder="Manpower requirement details, project location, and deployment timeline"
                            aria-label="Requirement Details"
                            value={inqMessage}
                            onChange={(e) => setInqMessage(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="text-center">
                        <button
                          type="submit"
                          className="rec-btn-primary"
                          style={{ border: "none", cursor: "pointer" }}
                          disabled={inqSubmitting}
                        >
                          {inqSubmitting ? "Sending…" : "Submit Inquiry"}
                          <i className="fa fa-arrow-right" aria-hidden="true" />
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </LayoutOne>
    </Fragment>
  );
};

export default OverseasRecruitment;
